import { useMemo } from 'react';
import { useUpdate } from '../../engine/api/hooks';
import { Timeline, type TimelineItem } from '../../engine/views/Timeline';
import { Calendar, type CalendarEvent } from '../../engine/views/Calendar';
import { EmptyState } from '../../engine/design/primitives/Misc';
import { STATUS_TONE, day, taskProgress, useProjectDetail, type PhaseRow, type TaskRow } from './data';

// Timeline (gantt) over the engine Timeline: phases as summary bars, tasks grouped under
// their phase, dependency arrows from task_dependency, progress fills, drag/resize → PATCH
// dates (phase rows PATCH the phase). Calendar: tasks by due date.

const PHASE_PREFIX = 'phase:';

export function TasksTimeline({ projectId, onOpen }: { projectId: string; onOpen: (id: string) => void }) {
  const detail = useProjectDetail(projectId);
  const updateTask = useUpdate<TaskRow>('project-tasks', { toast: { success: 'Rescheduled' }, onSuccess: detail.refresh });
  const updatePhase = useUpdate<PhaseRow>('project-phases', { toast: { success: 'Phase rescheduled' }, onSuccess: detail.refresh });

  const items = useMemo<TimelineItem[]>(() => {
    const d = detail.data;
    if (!d) return [];
    const preds = new Map<string, string[]>();
    for (const dep of d.dependencies) (preds.get(dep.successor_id) ?? preds.set(dep.successor_id, []).get(dep.successor_id)!).push(dep.predecessor_id);
    const taskItem = (t: TaskRow, group: string): TimelineItem => ({
      id: t.id,
      label: `${t.ref} ${t.title}`,
      start: day(t.start_date) ?? day(t.due_date),
      end: day(t.due_date) ?? day(t.start_date),
      progress: taskProgress(t),
      dependsOn: preds.get(t.id) ?? [],
      group,
      tone: t.kind === 'milestone' && t.status !== 'done' ? 'accent' : STATUS_TONE[t.status],
    });
    const out: TimelineItem[] = [];
    for (const p of d.phases) {
      const tasks = d.tasks.filter((t) => t.phase_id === p.id);
      const starts = tasks.map((t) => day(t.start_date)).filter((x): x is string => Boolean(x));
      const ends = tasks.map((t) => day(t.due_date)).filter((x): x is string => Boolean(x));
      out.push({
        id: `${PHASE_PREFIX}${p.id}`,
        label: <strong>{p.name}</strong>,
        start: day(p.start_date) ?? (starts.length ? starts.reduce((a, b) => (a < b ? a : b)) : null),
        end: day(p.end_date) ?? (ends.length ? ends.reduce((a, b) => (a > b ? a : b)) : null),
        tone: 'muted',
        group: 'phase',
      });
      for (const t of tasks) out.push(taskItem(t, p.name));
    }
    for (const t of d.tasks.filter((t) => !t.phase_id)) out.push(taskItem(t, 'No phase'));
    return out;
  }, [detail.data]);

  if (detail.data && detail.data.tasks.length === 0) return <EmptyState title="Nothing scheduled" icon="gantt-chart" hint="Give tasks start and due dates to plot them." />;
  return (
    <Timeline
      items={items}
      onOpen={(id) => !id.startsWith(PHASE_PREFIX) && onOpen(id)}
      onChange={(id, start, end) => {
        if (id.startsWith(PHASE_PREFIX)) {
          const pid = id.slice(PHASE_PREFIX.length);
          const p = detail.data?.phases.find((x) => x.id === pid);
          updatePhase.mutate({ id: pid, body: { start_date: start, end_date: end }, previous: p ? { start_date: p.start_date, end_date: p.end_date } : undefined });
          return;
        }
        const t = detail.data?.tasks.find((x) => x.id === id);
        updateTask.mutate({ id, body: { start_date: start, due_date: end }, previous: t ? { start_date: t.start_date, due_date: t.due_date } : undefined });
      }}
    />
  );
}

export function TasksCalendar({ projectId, onOpen }: { projectId: string; onOpen: (id: string) => void }) {
  const detail = useProjectDetail(projectId);
  const events = useMemo<CalendarEvent[]>(
    () => (detail.data?.tasks ?? []).filter((t) => t.due_date).map((t) => ({ id: t.id, label: `${t.ref} ${t.title}`, start: day(t.due_date)!, tone: STATUS_TONE[t.status] })),
    [detail.data?.tasks],
  );
  return <Calendar events={events} onOpen={onOpen} />;
}
